import { message, Radio, Tooltip, Upload, Tabs, Button, Divider, Spin, Popover } from 'antd';
import FeatherIcon from 'feather-icons-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import QuoteStore from '../../stores/QuoteStore';
import PartStore from '../../stores/PartStore';
import { getAutoUploadPart, isPartLimitReached, partLimitMessage } from '../../utils/autoUpload';
import { AssociateParts } from './AssociateParts';
import SplitPDF from '../SplitPDF';
import { RcFile, UploadProps } from 'antd/es/upload';
import { observer } from 'mobx-react';
//Copied from zip.js
import { allowed_2d, allowed_3d, getMimeType, post } from '@partsbadger/utils';
import {
    FolderOutlined,
    UploadOutlined,
    LoadingOutlined,
    SplitCellsOutlined,
    FilePdfOutlined,
} from '@ant-design/icons';

import JSZip from 'jszip';
import ITARNotice from '../ITARNotice';
import { ITARContactSalesForm, ITAR_YES_EXPLANATION_STAFF } from '@partsbadger/common-features';
import type { ITARUploadAnswer } from '@partsbadger/common-features';
import NestedZipModal, { ZipTreeEntry } from './NestedZipModal';

const MAX_ZIP_DEPTH = 3;
const ZIP_BATCH_DELAY_MS = 200;

interface Props {
    is_staff: boolean | undefined;
    showUploadTypeTabs: boolean;
    showExtraUploadInfo: boolean;
    showITARNotice: boolean;
}

const isHiddenFile = (name: string) => name.indexOf('._') !== -1;

const isZipExtension = (filename: string) => filename.split('.').pop()?.toLowerCase() === 'zip';

/**
 * Recursively scan a JSZip instance and build a tree, pre-expanding nested
 * zips up to `maxDepth` levels so the modal can show their contents.
 */
async function buildZipTree(
    zip: JSZip,
    prefix: string,
    depth: number
): Promise<{ tree: ZipTreeEntry[]; hasNestedZips: boolean }> {
    let hasNestedZips = false;
    const entries: ZipTreeEntry[] = [];

    const paths = Object.keys(zip.files).filter(p => !zip.files[p].dir);

    for (const relativePath of paths) {
        const name = relativePath.split('/').pop();
        if (!name || isHiddenFile(name)) continue;

        const key = `${prefix}/${relativePath}`;
        const isZip = isZipExtension(name);

        const entry: ZipTreeEntry = {
            key,
            name,
            path: relativePath,
            isZip,
            isDirectory: false,
        };

        if (isZip) {
            hasNestedZips = true;
            if (depth < MAX_ZIP_DEPTH) {
                try {
                    const blob = await zip.files[relativePath].async('blob');
                    const innerZip = await new JSZip().loadAsync(blob);
                    const inner = await buildZipTree(innerZip, key, depth + 1);
                    entry.children = inner.tree;
                } catch {
                    // If we can't read the inner zip, still show it as a zip entry
                }
            }
        }

        entries.push(entry);
    }

    return { tree: entries, hasNestedZips };
}

/**
 * Extract files from a JSZip and add them to PartStore.
 * When `includeNestedZips` is true, recursively extract inner zips.
 */
async function extractAndAddFiles(zip: JSZip, includeNestedZips: boolean, depth: number): Promise<void> {
    const paths = Object.keys(zip.files).filter(p => !zip.files[p].dir);

    for (const relativePath of paths) {
        if (isPartLimitReached(PartStore.count())) break;

        const name = relativePath.split('/').pop();
        if (!name || isHiddenFile(name)) continue;

        if (isZipExtension(name)) {
            if (includeNestedZips && depth < MAX_ZIP_DEPTH) {
                try {
                    const blob = await zip.files[relativePath].async('blob');
                    const innerZip = await new JSZip().loadAsync(blob);
                    await extractAndAddFiles(innerZip, true, depth + 1);
                } catch {
                    message.warning(`Could not extract inner ZIP: ${name}`);
                }
            }
            continue;
        }

        const blob = await zip.files[relativePath].async('blob');
        const fileType = getMimeType(name);
        const file = new File([blob], name, { type: fileType });
        PartStore.addPart(file);
    }
}

interface PendingNestedZip {
    zip: JSZip;
    name: string;
    entries: ZipTreeEntry[];
}

export const UploadPartFilesMultiple = observer((props: Props) => {
    const [uploadType, setUploadType] = useState('file');
    const [PDFFile, setPDFFile] = useState<File>();
    const [itarUploadAnswer, setITARUploadAnswer] = useState<ITARUploadAnswer>(
        QuoteStore.quote?.itar_notice_answer ?? null
    );
    const [showITARRequiredError, setShowITARRequiredError] = useState(false);
    const [itarFocusSignal, setITARFocusSignal] = useState(0);

    useEffect(() => {
        if (!QuoteStore.quote) {
            setITARUploadAnswer(null);
            return;
        }
        if (QuoteStore.quote.itar_notice_answer !== undefined) {
            setITARUploadAnswer(QuoteStore.quote.itar_notice_answer ?? null);
        }
    }, [QuoteStore.quote, QuoteStore.quote?.itar_notice_answer]);

    const pendingZipsRef = useRef<PendingNestedZip[]>([]);
    const batchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [batchedZips, setBatchedZips] = useState<PendingNestedZip[]>([]);
    const [extractingZip, setExtractingZip] = useState(false);
    // ZIPs being read: more files are coming, so the first one must not upload on its own.
    const [unzipping, setUnzipping] = useState(0);

    const showBatchedModal = useCallback(() => {
        const batch = pendingZipsRef.current.splice(0);
        if (batch.length > 0) {
            setBatchedZips(batch);
        }
    }, []);

    const closeModal = useCallback(() => {
        setBatchedZips([]);
        setExtractingZip(false);
    }, []);

    const handleExtractAll = useCallback(async () => {
        if (batchedZips.length === 0) return;
        setExtractingZip(true);
        try {
            for (const pending of batchedZips) {
                await extractAndAddFiles(pending.zip, true, 0);
            }
        } finally {
            closeModal();
        }
    }, [batchedZips, closeModal]);

    const handleSkipInnerZips = useCallback(async () => {
        if (batchedZips.length === 0) return;
        setExtractingZip(true);
        try {
            for (const pending of batchedZips) {
                await extractAndAddFiles(pending.zip, false, 0);
            }
        } finally {
            closeModal();
        }
    }, [batchedZips, closeModal]);

    const UnZipFiles = async (zip_file: RcFile) => {
        setUnzipping(count => count + 1);
        try {
            const zip = await new JSZip().loadAsync(zip_file);
            const { tree, hasNestedZips } = await buildZipTree(zip, zip_file.name, 0);

            if (hasNestedZips) {
                pendingZipsRef.current.push({ zip, name: zip_file.name, entries: tree });
                if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
                batchTimerRef.current = setTimeout(showBatchedModal, ZIP_BATCH_DELAY_MS);
            } else {
                await extractAndAddFiles(zip, false, 0);
            }
        } finally {
            setUnzipping(count => count - 1);
        }
    };

    const uploadProps: UploadProps = {
        name: 'file',
        multiple: true,
        showUploadList: false,
        beforeUpload: (file: RcFile) => {
            if (props.showITARNotice && itarUploadAnswer !== false) {
                setShowITARRequiredError(true);
                setITARFocusSignal(signal => signal + 1);
                return false;
            }

            const isLt2M = file.size / 1024 / 1024 < 100;

            if (!isLt2M) {
                message.error('File must smaller than 100MB!', 10);
                return false;
            }

            if (isPartLimitReached(PartStore.count())) {
                message.error(partLimitMessage);
                return false;
            }

            if (PartStore.isZipFile(file.name)) {
                UnZipFiles(file);
            } else if (uploadType !== 'split') {
                PartStore.addPart(file);
            }

            if (uploadType === 'split') {
                setPDFFile(file);
                handleClickScroll('splitPDF');
            }

            return false;
        },
    };

    const tabs = [
        {
            label: 'Upload Files',
            key: 'file',
        },
        {
            label: (
                <>
                    <FolderOutlined />
                    Upload Folder
                </>
            ),
            key: 'directory',
        },
    ];

    useEffect(() => {
        const timeout = setTimeout(() => {
            const part = getAutoUploadPart(PartStore.fileParts, {
                busy: unzipping > 0 || extractingZip,
                forStaff: QuoteStore.for_staff,
            });
            if (!part) return;

            if (QuoteStore.for_staff) {
                PartStore.enqueueUploads([part]);
            } else {
                PartStore.uploadPart(part);
            }
        }, 500);

        return () => {
            clearTimeout(timeout);
        };
    }, [PartStore.fileParts, unzipping, extractingZip]);

    function resetSplitFunction() {
        setUploadType('file');
        setPDFFile(undefined);
    }

    const handleClickScroll = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const splitPDF = (
        <div id="splitPDF">
            {PDFFile && props.is_staff ? <SplitPDF handleReset={() => resetSplitFunction()} pdfFile={PDFFile} /> : null}
        </div>
    );

    const handleITARUploadAnswerChange = (answer: ITARUploadAnswer) => {
        setITARUploadAnswer(answer);
        setShowITARRequiredError(false);
        QuoteStore.updateQuote({ itar_notice_answer: answer });
    };

    const isITARUploadBlocked = props.showITARNotice && itarUploadAnswer !== false;
    const showITARContactSalesForm = props.showITARNotice && itarUploadAnswer === true && !props.is_staff;

    const handleBlockedITARUploadIntent = () => {
        if (!isITARUploadBlocked) return;

        setShowITARRequiredError(true);
        setITARFocusSignal(signal => signal + 1);
    };

    const splitPDFButton = (
        <>
            {props.is_staff ? (
                <Button
                    type={'ghost'}
                    className="d-flex flex-row align-items-center mx-auto mb-2 shadow-light"
                    icon={<FilePdfOutlined />}
                    disabled={isITARUploadBlocked}
                    onClick={e => setUploadType('split')}
                >
                    {'Split PDF File'}
                </Button>
            ) : null}
        </>
    );

    return (
        <div className="mb-2">
            {props.showITARNotice ? (
                <ITARNotice
                    value={itarUploadAnswer}
                    onChange={handleITARUploadAnswerChange}
                    showRequiredError={showITARRequiredError}
                    focusSignal={itarFocusSignal}
                    yesExplanation={props.is_staff ? ITAR_YES_EXPLANATION_STAFF : undefined}
                />
            ) : null}

            {showITARContactSalesForm ? (
                <ITARContactSalesForm
                    onSubmit={async msg => {
                        if (!QuoteStore.quote_uid) throw new Error('No active quote. Please try again.');
                        await post(`/customer/quote/${QuoteStore.quote_uid}/itar-contact/`, { message: msg });
                    }}
                />
            ) : (
                <div onClickCapture={handleBlockedITARUploadIntent}>
                    {props.showUploadTypeTabs ? (
                        <div className="mb-2">
                            <Tabs
                                centered={true}
                                defaultActiveKey={uploadType}
                                onChange={e => setUploadType(e)}
                                items={tabs}
                            />
                        </div>
                    ) : null}

                    {props.showExtraUploadInfo ? (
                        <div className="d-flex align-items-center text-muted">
                            <div className="d-flex align-items-center">
                                <FeatherIcon icon="lock" size={14} className="mr-1" />
                                <span>File uploads are secure and confidential</span>
                            </div>
                            <div className="d-flex align-items-center ml-auto">
                                <FeatherIcon icon="award" size={14} className="mr-1" />
                                <span>ISO 9001:2015 and AS 9100 Certified</span>
                            </div>
                        </div>
                    ) : null}

                    <div
                        className="upload-more-parts-card"
                        data-testid="otf-upload-dropzone"
                        style={PDFFile ? { display: 'none' } : undefined}
                    >
                        <div className="upload-more-parts-card-header">
                            <div>
                                <div className="upload-more-parts-card-title">Add more parts</div>
                                <div className="upload-more-parts-card-description">
                                    Upload a 2D or 3D file to add another item to this quote.
                                </div>
                            </div>
                        </div>
                        <Upload.Dragger
                            {...uploadProps}
                            directory={uploadType === 'directory'}
                            className="w-100 upload-part-files-dragger"
                            disabled={!!PartStore.fileParts.find(filePart => filePart.uploading) || isITARUploadBlocked}
                        >
                            <>
                            {PartStore.fileParts.find(filePart => filePart.uploading) ? (
                                <Spin
                                    spinning={true}
                                    tip={
                                        PartStore.fileParts.find(filePart => filePart.uploading)
                                            ? 'Uploading Files...'
                                            : 'Uploading File...'
                                    }
                                    indicator={<LoadingOutlined />}
                                    className="my-4"
                                />
                            ) : (
                                <>
                                    {QuoteStore.quote?.line_items.length === 0 ? (
                                        <div className="py-4">
                                            <p
                                                className="ant-upload-text pb-2"
                                                style={{ fontSize: '1.6rem', fontWeight: 600 }}
                                            >
                                                Select your part files
                                            </p>

                                            <p className="ant-upload-hint w-full m-auto">
                                                {uploadType === 'directory' ? (
                                                    <span>You can select and upload a whole folder.</span>
                                                ) : (
                                                    <span>Drag and drop your 2D and 3D files or zip files.</span>
                                                )}
                                            </p>

                                            <div style={{ width: '6rem', margin: '0 auto' }}>
                                                <Divider>or</Divider>
                                            </div>

                                            <Button
                                                type={'primary'}
                                                className="d-flex flex-row align-items-center mx-auto mb-2 shadow-light"
                                                icon={<FeatherIcon icon="upload-cloud" size="22" className="mr-2" />}
                                                disabled={isITARUploadBlocked}
                                                onClick={() => setUploadType('file')}
                                            >
                                                {uploadType === 'directory' ? 'Upload Folder' : 'Upload Files'}
                                            </Button>
                                            {splitPDFButton}
                                            <p className="pt-3">
                                                <span className="text-uppercase">
                                                    {allowed_3d.replace(/,/g, ',  ')}
                                                    {',  '}
                                                    {allowed_2d.replace(/,/g, ',  ')}
                                                </span>
                                                <br />
                                                <span>100 MB Max File Size</span>
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column mb-3 align-items-center justify-content-center ant-upload-text">
                                            <br />
                                            <FeatherIcon icon="upload-cloud" size="24" className="mr-2 text-primary" />
                                            <span className="text-primary mr-1">Click to add another part</span>
                                            <span>or drag a file here</span>
                                            {props.is_staff ? (
                                                <div style={{ width: '6rem', margin: '0 auto' }}>
                                                    <Divider>or</Divider>
                                                </div>
                                            ) : null}
                                            {splitPDFButton}
                                            <p className="pt-3">
                                                <span className="text-uppercase">
                                                    {allowed_3d.replace(/,/g, ',  ')}
                                                    {',  '}
                                                    {allowed_2d.replace(/,/g, ',  ')}
                                                </span>
                                                <br />
                                                <span>100 MB Max File Size</span>
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                            </>
                        </Upload.Dragger>
                    </div>
                </div>
            )}

            <AssociateParts is_staff={props.is_staff} />

            {splitPDF}

            <NestedZipModal
                visible={batchedZips.length > 0}
                groups={batchedZips.map(z => ({ name: z.name, entries: z.entries }))}
                loading={extractingZip}
                onExtractAll={handleExtractAll}
                onSkipInnerZips={handleSkipInnerZips}
                onCancel={closeModal}
            />
        </div>
    );
});
